import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  Typography,
  TextField,
  Stepper,
  Step,
  StepLabel,
  Grid,
  MenuItem,
  Divider,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Paper,
  Chip,
  Alert,
  CircularProgress,
  LinearProgress,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  CreditCard as CreditCardIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  Star as StarIcon,
  CardGiftcard as ReferralIcon,
  AutoAwesome as AIIcon,
  VpnKey as KeyIcon,
  Error as ErrorIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useToast } from '../contexts/ToastContext';
import { useAuth } from '../contexts/AuthContext';
import organizationService from '../services/organizationService';
import { PUBLIC_PLANS } from '../services/subscriptionService';
import { processReferralSignup } from '../services/referralService';
import { validateInviteKey, markInviteKeyUsed, InviteKey } from '../services/inviteKeyService';
import { LegalAcceptance } from '../components/legal';
import { trackSignup, getStoredUTMParams } from '../utils/analytics';

// Grid wrappers for MUI v7 compatibility
const GridContainer = (props: any) => <Grid container {...props} />;
const GridItem = ({ xs, sm, md, lg, xl, ...props }: any) => {
  const size: any = {};
  if (xs !== undefined) size.xs = xs;
  if (sm !== undefined) size.sm = sm;
  if (md !== undefined) size.md = md;
  if (lg !== undefined) size.lg = lg;
  if (xl !== undefined) size.xl = xl;
  return <Grid size={Object.keys(size).length ? size : undefined} {...props} />;
};

// Helper function to detect AI features for consistent styling
const isAIFeature = (feature: string): boolean => {
  const featureLower = feature.toLowerCase();
  return featureLower.includes('transcri') ||
         featureLower.includes('insight') ||
         featureLower.includes(' ia') ||
         featureLower.includes('notas automaticas');
};

/**
 * Password Strength Calculator
 */
const getPasswordStrength = (password: string): { score: number; label: string; color: string } => {
  let score = 0;
  if (password.length >= 8) score++;
  if (/[A-Z]/.test(password)) score++;
  if (/[a-z]/.test(password)) score++;
  if (/[0-9]/.test(password)) score++;
  if (/[^A-Za-z0-9]/.test(password)) score++;

  if (score <= 1) return { score: 20, label: 'Fraca', color: '#f44336' };
  if (score === 2) return { score: 40, label: 'Fraca', color: '#f44336' };
  if (score === 3) return { score: 60, label: 'Média', color: '#ff9800' };
  if (score === 4) return { score: 80, label: 'Forte', color: '#4caf50' };
  return { score: 100, label: 'Muito forte', color: '#2e7d32' };
};

/**
 * Business Types
 */
const BUSINESS_TYPES = [
  { value: 'psychology', label: 'Psicologia' },
  { value: 'dentistry', label: 'Odontologia' },
  { value: 'medical', label: 'Medicina' },
  { value: 'salon', label: 'Salão de Beleza' },
  { value: 'legal', label: 'Advocacia' },
  { value: 'coaching', label: 'Coaching' },
  { value: 'other', label: 'Outro' },
];

/**
 * Steps - dynamic based on invite key presence
 */
const STEPS_WITH_INVITE = ['Organização', 'Administrador'];
const STEPS_WITHOUT_INVITE = ['Organização', 'Administrador', 'Plano'];

/**
 * Organization Signup Page
 * Multi-step wizard for creating new organizations
 */
export const OrganizationSignup: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { login } = useAuth();
  const [searchParams] = useSearchParams();
  const [activeStep, setActiveStep] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [referralCode, setReferralCode] = useState<string | null>(null);

  // Invite key state
  const [inviteCode, setInviteCode] = useState<string | null>(null);
  const [validatedInvite, setValidatedInvite] = useState<InviteKey | null>(null);
  const [inviteError, setInviteError] = useState<string | null>(null);
  const [isValidatingInvite, setIsValidatingInvite] = useState(false);

  // Public trial mode - when ?trial=true is in URL (no invite key needed)
  const [isPublicTrial, setIsPublicTrial] = useState(false);

  // Determine steps based on invite key or public trial
  const steps = validatedInvite ? STEPS_WITH_INVITE : STEPS_WITHOUT_INVITE;

  // Get referral code and invite code from URL on mount
  useEffect(() => {
    const ref = searchParams.get('ref');
    if (ref) {
      setReferralCode(ref);
    }

    // FAIL-CLOSED para cobrança: o fluxo pago só é ativado quando o usuário
    // EXPLICITAMENTE escolheu um plano via PricingPage (?plan=X) ou tem um
    // invite key (?invite=X). Qualquer outro caminho — Navbar, /login link,
    // ExitIntentPopup, LPs antigas, tráfego pago, links de indicação, ou
    // entrada direta digitando /signup — cai automaticamente no trial grátis
    // de 30 dias. Isso impede que um CTA novo "Testar Grátis" criado sem
    // ?trial=true vire armadilha de cobrança.
    const trialParam = searchParams.get('trial');
    const planParam = searchParams.get('plan');
    const inviteParam = searchParams.get('invite');
    // Aceitar `?plan=X` só quando X é um plano público conhecido. Antes, qualquer
    // string truthy (`?plan=foo`, `?plan=clinica` em vez de `small_clinic`,
    // link estourado, copy-paste quebrado) bypassava o trial e mandava pra
    // checkout do Individual — re-introduzindo a fail-open behavior.
    const knownPaidPlan = !!planParam && PUBLIC_PLANS.some(p => p.name === planParam);
    const shouldActivateTrial = trialParam === 'true' || (!knownPaidPlan && !inviteParam);

    if (shouldActivateTrial) {
      setIsPublicTrial(true);
      // Virtual invite key for public trial — Individual plan, 30 days FREE
      setValidatedInvite({
        id: 'public-trial',
        code: 'PUBLIC-TRIAL-30',
        planId: 'individual',
        trialDays: 30,
        maxUses: -1, // Unlimited for public trial
        currentUses: 0,
        isActive: true,
        expiresAt: null,
        createdBy: null,
        notes: 'Public trial signup - 30 days free Individual plan',
        createdAt: new Date().toISOString(),
        updatedAt: new Date().toISOString(),
      });
      setInviteError(null);
      return; // Skip invite key check
    }

    // Check for invite key
    const invite = inviteParam;
    if (invite) {
      setInviteCode(invite);
      setIsValidatingInvite(true);

      // Validate the invite key
      validateInviteKey(invite).then(result => {
        setIsValidatingInvite(false);
        if (result.valid && result.key) {
          setValidatedInvite(result.key);
          setInviteError(null);
        } else {
          setInviteError(result.error || 'Chave de convite inválida');
          setValidatedInvite(null);
        }
      }).catch(() => {
        setIsValidatingInvite(false);
        setInviteError('Erro ao validar chave de convite');
      });
    }
  }, [searchParams]);

  // Step 1: Organization data
  const [orgData, setOrgData] = useState({
    name: '',
    slug: '',
    businessType: 'psychology' as string,
  });

  const [orgErrors, setOrgErrors] = useState({
    name: '',
    slug: '',
  });

  // Step 2: Admin data
  const [adminData, setAdminData] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });

  const [adminErrors, setAdminErrors] = useState({
    fullName: '',
    email: '',
    phone: '',
    password: '',
    confirmPassword: '',
  });

  // Legal acceptance state
  const [legalAccepted, setLegalAccepted] = useState(false);
  const [legalError, setLegalError] = useState('');

  // Step 3: Plan selection.
  // Quando o usuário chega via PricingPage com ?plan=X, pre-selecionamos
  // esse plano (caso contrário, default = primeiro PUBLIC_PLAN).
  const [selectedPlan, setSelectedPlan] = useState<string>(() => {
    const planParam = searchParams.get('plan');
    if (planParam && PUBLIC_PLANS.some(p => p.name === planParam)) {
      return planParam;
    }
    return PUBLIC_PLANS.length > 0 ? PUBLIC_PLANS[0].name : 'individual';
  });

  // Validation functions
  const validateOrgName = (name: string): string => {
    if (!name) return 'Nome da organização é obrigatório';
    if (name.length < 3) return 'Nome deve ter pelo menos 3 caracteres';
    return '';
  };

  const validateSlug = (slug: string): string => {
    if (!slug) return 'Slug é obrigatório';
    if (slug.length < 3) return 'Slug deve ter pelo menos 3 caracteres';
    if (!/^[a-z0-9-]+$/.test(slug)) return 'Use apenas letras minúsculas, números e -';
    return '';
  };

  const validateFullName = (name: string): string => {
    if (!name) return 'Nome completo é obrigatório';
    if (name.length < 3) return 'Nome deve ter pelo menos 3 caracteres';
    return '';
  };

  const validateEmail = (email: string): string => {
    if (!email) return 'Email é obrigatório';
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(email)) return 'Email inválido';
    return '';
  };

  const validatePhone = (phone: string): string => {
    if (!phone) return 'Telefone é obrigatório';
    const digits = phone.replace(/\D/g, '');
    if (digits.length < 10 || digits.length > 13) return 'Telefone inválido (use DDD + número)';
    return '';
  };

  const validatePassword = (password: string): string => {
    if (!password) return 'Senha é obrigatória';
    if (password.length < 8) return 'Senha deve ter pelo menos 8 caracteres';
    return '';
  };

  const validateConfirmPassword = (confirmPassword: string, password: string): string => {
    if (!confirmPassword) return 'Confirmação de senha é obrigatória';
    if (confirmPassword !== password) return 'As senhas não coincidem';
    return '';
  };

  // Auto-generate slug from organization name
  const handleOrgNameChange = (name: string) => {
    setOrgData({
      ...orgData,
      name,
      slug: name
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .replace(/[^a-z0-9\s-]/g, '')
        .replace(/\s+/g, '-')
        .replace(/-+/g, '-')
        .trim(),
    });

    const error = validateOrgName(name);
    setOrgErrors({ ...orgErrors, name: error });
  };

  const handleOrgSlugChange = (slug: string) => {
    setOrgData({ ...orgData, slug });
    const error = validateSlug(slug);
    setOrgErrors({ ...orgErrors, slug: error });
  };

  const handleAdminFieldChange = (field: keyof typeof adminData, value: string) => {
    setAdminData({ ...adminData, [field]: value });

    let error = '';
    if (field === 'fullName') error = validateFullName(value);
    if (field === 'email') error = validateEmail(value);
    if (field === 'phone') error = validatePhone(value);
    if (field === 'password') error = validatePassword(value);
    if (field === 'confirmPassword') error = validateConfirmPassword(value, adminData.password);

    setAdminErrors({ ...adminErrors, [field]: error });
  };

  // Validate current step
  const validateStep = (step: number): boolean => {
    if (step === 0) {
      const errors = {
        name: validateOrgName(orgData.name),
        slug: isPublicTrial ? '' : validateSlug(orgData.slug),
      };
      setOrgErrors(errors);
      return !errors.name && !errors.slug;
    }

    if (step === 1) {
      const errors = {
        fullName: validateFullName(adminData.fullName),
        email: validateEmail(adminData.email),
        phone: validatePhone(adminData.phone),
        password: validatePassword(adminData.password),
        confirmPassword: validateConfirmPassword(adminData.confirmPassword, adminData.password),
      };
      setAdminErrors(errors);

      // Validate legal acceptance
      if (!legalAccepted) {
        setLegalError('Você deve aceitar os Termos de Uso e Política de Privacidade');
      } else {
        setLegalError('');
      }

      return !errors.fullName && !errors.email && !errors.phone && !errors.password && !errors.confirmPassword && legalAccepted;
    }

    // Validate step 2 (plan selection) - only shown without invite key
    if (step === 2) {
      // Check if a valid plan is selected (not trial, must be in PUBLIC_PLANS)
      const validPlanIds = PUBLIC_PLANS.map(p => p.name);
      if (!selectedPlan || !validPlanIds.includes(selectedPlan as any)) {
        toast.showWarning('Por favor, selecione um plano');
        return false;
      }
      return true;
    }

    return true;
  };

  // Handle next step
  const handleNext = () => {
    if (!validateStep(activeStep)) {
      toast.showWarning('Por favor, corrija os erros antes de continuar');
      return;
    }

    setActiveStep((prevStep) => prevStep + 1);
  };

  // Handle back step
  const handleBack = () => {
    setActiveStep((prevStep) => prevStep - 1);
  };

  // Handle signup completion
  const handleSignup = async () => {
    if (!validateStep(activeStep)) {
      toast.showWarning('Por favor, corrija os erros antes de continuar');
      return;
    }

    setIsLoading(true);

    try {
      // Create organization via organizationService
      const result = await organizationService.createOrganization({
        name: orgData.name,
        businessType: orgData.businessType as any,
        adminEmail: adminData.email,
        adminName: adminData.fullName,
        adminPhone: adminData.phone,
        // Pass selected plan (only used if no invite key)
        selectedPlanId: selectedPlan,
        // Pass invite key if validated
        inviteKey: validatedInvite ? {
          planId: validatedInvite.planId,
          trialDays: validatedInvite.trialDays,
          keyId: validatedInvite.id,
        } : undefined,
      });

      if (!result) {
        throw new Error('Falha ao criar organização');
      }

      const { organization, user: _user } = result;

      // Mark invite key as used (only for real invite keys, not public trial)
      if (validatedInvite && !isPublicTrial) {
        try {
          await markInviteKeyUsed(validatedInvite.id, organization.id);
        } catch (inviteError) {
          console.warn('Error marking invite key as used:', inviteError);
        }
      }

      // Process referral if there's a referral code
      if (referralCode) {
        try {
          await processReferralSignup(referralCode, organization.id);
        } catch (refError) {
          console.warn('Error processing referral:', refError);
        }
      }

      // Build success message
      let successMessage = `Organização "${organization.name}" criada com sucesso!`;
      let loginMessage = 'Organização criada! Faça login para continuar.';

      if (validatedInvite) {
        successMessage += ` ${validatedInvite.trialDays} dias grátis do plano Individual ativados!`;
        loginMessage = `Organização criada com ${validatedInvite.trialDays} dias grátis! Faça login para continuar.`;
      }

      if (referralCode) {
        successMessage += ' Bônus de indicação aplicado!';
        if (!validatedInvite) {
          loginMessage = 'Organização criada com bônus de indicação! Faça login para continuar.';
        }
      }

      toast.showSuccess(successMessage);

      // Fire analytics conversion event with the actual plan and original UTM source
      const utms = getStoredUTMParams();
      trackSignup(organization.planId, utms.utm_source || (referralCode ? 'referral' : 'direct'));

      // Determine where to redirect based on ACTUAL organization data (not UI state)
      const isPaidPlan = organization.planId !== 'trial';
      const hasActiveTrialPeriod = organization.subscriptionStatus === 'trialing';

      // For paid plans, do automatic login before redirecting to checkout
      // This is needed because checkout is a protected route that requires authentication
      if (isPaidPlan && !hasActiveTrialPeriod) {
        // Do automatic login with user credentials
        const loginSuccess = await login(adminData.email, adminData.password);

        if (loginSuccess) {
          // Small delay to ensure auth state is updated
          setTimeout(() => {
            navigate('/subscription/checkout', {
              state: {
                organizationId: organization.id,
                planId: organization.planId,
                userId: result.user?.id,
                email: adminData.email,
                organizationName: organization.name,
                fromSignup: true,
              },
            });
          }, 500);
        } else {
          toast.showWarning('Conta criada! Faça login para continuar com o pagamento.');
          navigate('/login', {
            state: {
              email: adminData.email,
              message: 'Conta criada! Faça login para continuar com o pagamento.',
            },
          });
        }
      } else {
        // Trial plan or has active trial period: send to dedicated success page
        // (fires Lead/conversion pixel and gives the user a clear next step).
        setTimeout(() => {
          navigate('/signup/success', {
            state: {
              email: adminData.email,
              message: loginMessage,
              planId: organization.planId,
              organizationName: organization.name,
            },
          });
        }, 1500);
      }
    } catch (err) {
      toast.showError(
        err instanceof Error ? err.message : 'Erro ao criar organização'
      );
    } finally {
      setIsLoading(false);
    }
  };

  // Get step content
  const getStepContent = (step: number) => {
    switch (step) {
      case 0:
        return (
          <Box>
            <Box display="flex" alignItems="center" mb={3}>
              <BusinessIcon sx={{ mr: 1, color: 'primary.main', fontSize: 32 }} />
              <Typography variant="h6">Informações da Organização</Typography>
            </Box>

            <GridContainer spacing={3}>
              <GridItem xs={12}>
                <TextField
                  label="Nome da Organização"
                  value={orgData.name}
                  onChange={(e) => handleOrgNameChange(e.target.value)}
                  fullWidth
                  required
                  error={!!orgErrors.name}
                  helperText={orgErrors.name || 'Nome completo da sua organização'}
                  autoFocus
                />
              </GridItem>

              {!isPublicTrial && (
                <GridItem xs={12}>
                  <TextField
                    label="Slug (URL)"
                    value={orgData.slug}
                    onChange={(e) => handleOrgSlugChange(e.target.value)}
                    fullWidth
                    required
                    error={!!orgErrors.slug}
                    helperText={
                      orgErrors.slug ||
                      `URL: clinafy.com/${orgData.slug || 'seu-slug'}`
                    }
                  />
                </GridItem>
              )}

              {!isPublicTrial && (
                <GridItem xs={12}>
                  <TextField
                    label="Tipo de Negócio"
                    select
                    value={orgData.businessType}
                    onChange={(e) =>
                      setOrgData({ ...orgData, businessType: e.target.value })
                    }
                    fullWidth
                    required
                  >
                    {BUSINESS_TYPES.map((type) => (
                      <MenuItem key={type.value} value={type.value}>
                        {type.label}
                      </MenuItem>
                    ))}
                  </TextField>
                </GridItem>
              )}
            </GridContainer>
          </Box>
        );

      case 1:
        return (
          <Box>
            <Box display="flex" alignItems="center" mb={3}>
              <PersonIcon sx={{ mr: 1, color: 'primary.main', fontSize: 32 }} />
              <Typography variant="h6">Dados do Administrador</Typography>
            </Box>

            <GridContainer spacing={3}>
              <GridItem xs={12}>
                <TextField
                  label="Nome Completo"
                  value={adminData.fullName}
                  onChange={(e) => handleAdminFieldChange('fullName', e.target.value)}
                  fullWidth
                  required
                  error={!!adminErrors.fullName}
                  helperText={adminErrors.fullName}
                  autoFocus
                />
              </GridItem>

              <GridItem xs={12}>
                <TextField
                  label="Email"
                  type="email"
                  value={adminData.email}
                  onChange={(e) => handleAdminFieldChange('email', e.target.value)}
                  fullWidth
                  required
                  error={!!adminErrors.email}
                  helperText={adminErrors.email || 'Email para login'}
                />
              </GridItem>

              <GridItem xs={12}>
                <TextField
                  label="Telefone (WhatsApp)"
                  type="tel"
                  value={adminData.phone}
                  onChange={(e) => handleAdminFieldChange('phone', e.target.value)}
                  fullWidth
                  required
                  error={!!adminErrors.phone}
                  helperText={adminErrors.phone || 'Com DDD. Usamos para suporte e avisos importantes.'}
                  placeholder="(41) 99999-9999"
                />
              </GridItem>

              <GridItem xs={12} md={6}>
                <TextField
                  label="Senha"
                  type="password"
                  value={adminData.password}
                  onChange={(e) => handleAdminFieldChange('password', e.target.value)}
                  fullWidth
                  required
                  error={!!adminErrors.password}
                  helperText={adminErrors.password || 'Mínimo 8 caracteres'}
                />
                {adminData.password && (
                  <Box sx={{ mt: 1 }}>
                    <LinearProgress
                      variant="determinate"
                      value={getPasswordStrength(adminData.password).score}
                      sx={{
                        height: 6,
                        borderRadius: 3,
                        bgcolor: 'grey.200',
                        '& .MuiLinearProgress-bar': {
                          borderRadius: 3,
                          bgcolor: getPasswordStrength(adminData.password).color,
                        },
                      }}
                    />
                    <Typography
                      variant="caption"
                      sx={{
                        color: getPasswordStrength(adminData.password).color,
                        fontWeight: 600,
                        mt: 0.5,
                        display: 'block',
                      }}
                    >
                      Senha: {getPasswordStrength(adminData.password).label}
                    </Typography>
                  </Box>
                )}
              </GridItem>

              <GridItem xs={12} md={6}>
                <TextField
                  label="Confirmar Senha"
                  type="password"
                  value={adminData.confirmPassword}
                  onChange={(e) => handleAdminFieldChange('confirmPassword', e.target.value)}
                  fullWidth
                  required
                  error={!!adminErrors.confirmPassword}
                  helperText={adminErrors.confirmPassword}
                />
              </GridItem>

              {/* Legal Acceptance - LGPD Compliance */}
              <GridItem xs={12}>
                <Divider sx={{ my: 2 }} />
                <LegalAcceptance
                  accepted={legalAccepted}
                  onChange={(accepted) => {
                    setLegalAccepted(accepted);
                    if (accepted) setLegalError('');
                  }}
                  error={legalError}
                />
              </GridItem>
            </GridContainer>
          </Box>
        );

      case 2:
        return (
          <Box>
            <Box display="flex" alignItems="center" mb={3}>
              <CreditCardIcon sx={{ mr: 1, color: 'primary.main', fontSize: 32 }} />
              <Typography variant="h6">Escolha seu Plano</Typography>
            </Box>

            <Typography variant="body2" color="textSecondary" sx={{ mb: 3 }}>
              Escolha o plano ideal para sua organização. Faça upgrade a qualquer momento.
            </Typography>

            <GridContainer spacing={2} justifyContent="center">
              {PUBLIC_PLANS.map((plan) => (
                <GridItem key={plan.name} xs={12} sm={6} md={3}>
                  <Card
                    sx={{
                      height: '100%',
                      cursor: 'pointer',
                      border: selectedPlan === plan.name ? 2 : 1,
                      borderColor: selectedPlan === plan.name ? 'primary.main' : 'divider',
                      position: 'relative',
                      '&:hover': {
                        borderColor: 'primary.light',
                      },
                    }}
                    onClick={() => setSelectedPlan(plan.name)}
                  >
                    {plan.popular && (
                      <Chip
                        icon={<StarIcon />}
                        label="Mais Popular"
                        color="secondary"
                        size="small"
                        sx={{
                          position: 'absolute',
                          top: 16,
                          right: 16,
                        }}
                      />
                    )}

                    <CardContent>
                      <Typography variant="h6" gutterBottom>
                        {plan.displayName}
                      </Typography>
                      <Typography variant="h4" gutterBottom>
                        R$ {plan.price.toFixed(2)}
                        <Typography variant="body2" component="span" color="textSecondary">
                          {' '}/ mês
                        </Typography>
                      </Typography>

                      <Divider sx={{ my: 2 }} />

                      <List dense>
                        {plan.features.map((feature, index) => {
                          const isAI = isAIFeature(feature);
                          return (
                            <ListItem key={index} disableGutters>
                              <ListItemIcon sx={{ minWidth: 32 }}>
                                {isAI ? (
                                  <AIIcon sx={{ color: '#9c27b0', fontSize: 18 }} />
                                ) : (
                                  <CheckIcon
                                    color={selectedPlan === plan.name ? 'primary' : 'success'}
                                    fontSize="small"
                                  />
                                )}
                              </ListItemIcon>
                              <ListItemText
                                primary={feature.replace('✨ ', '')}
                                primaryTypographyProps={{
                                  variant: 'body2',
                                  fontWeight: isAI ? 600 : 400,
                                  color: isAI ? '#7b1fa2' : 'text.primary',
                                }}
                              />
                            </ListItem>
                          );
                        })}
                      </List>

                      {selectedPlan === plan.name && (
                        <Box sx={{ mt: 2, textAlign: 'center' }}>
                          <Chip label="Selecionado" color="primary" />
                        </Box>
                      )}
                    </CardContent>
                  </Card>
                </GridItem>
              ))}
            </GridContainer>
          </Box>
        );

      default:
        return 'Unknown step';
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: 'background.default',
        py: 4,
      }}
    >
      <Box maxWidth="1400px" mx="auto" px={3}>
        {/* Header */}
        <Box textAlign="center" mb={4}>
          <Typography variant="h3" gutterBottom>
            Criar Nova Organização
          </Typography>
          <Typography variant="body1" color="textSecondary">
            {validatedInvite
              ? 'Complete seu cadastro em 2 passos rápidos'
              : 'Configure sua organização em 3 passos simples'}
          </Typography>

          {/* Public Trial or Invite Key Indicator */}
          {isPublicTrial && (
            <Box sx={{ mt: 3 }}>
              <Alert
                severity="success"
                icon={<StarIcon />}
                sx={{ display: 'inline-flex', borderRadius: 2, bgcolor: '#e8f5e9' }}
              >
                <Typography variant="body2" sx={{ fontWeight: 600 }}>
                  Teste gratuito de 30 dias do plano Individual! Sem cartão de crédito.
                </Typography>
              </Alert>
            </Box>
          )}

          {/* Invite Key Indicator (only if not public trial) */}
          {inviteCode && !isPublicTrial && (
            <Box sx={{ mt: 3 }}>
              {isValidatingInvite ? (
                <Paper
                  elevation={0}
                  sx={{
                    p: 2,
                    bgcolor: 'action.hover',
                    borderRadius: 2,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 1,
                  }}
                >
                  <CircularProgress size={20} />
                  <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                    Validando chave de convite...
                  </Typography>
                </Paper>
              ) : validatedInvite ? (
                <Alert
                  severity="success"
                  icon={<KeyIcon />}
                  sx={{ display: 'inline-flex', borderRadius: 2 }}
                >
                  <Typography variant="body2" sx={{ fontWeight: 600 }}>
                    Chave de convite válida! {validatedInvite.trialDays} dias grátis do plano Individual.
                  </Typography>
                </Alert>
              ) : inviteError ? (
                <Alert
                  severity="error"
                  icon={<ErrorIcon />}
                  sx={{ display: 'inline-flex', borderRadius: 2 }}
                >
                  <Typography variant="body2">
                    {inviteError}
                  </Typography>
                </Alert>
              ) : null}
            </Box>
          )}

          {/* Referral Code Indicator */}
          {referralCode && !inviteCode && !isPublicTrial && (
            <Paper
              elevation={0}
              sx={{
                mt: 3,
                p: 2,
                bgcolor: 'success.light',
                borderRadius: 2,
                display: 'inline-flex',
                alignItems: 'center',
                gap: 1,
              }}
            >
              <ReferralIcon sx={{ color: 'success.dark' }} />
              <Typography variant="body2" sx={{ color: 'success.dark', fontWeight: 600 }}>
                Código de indicação aplicado! Você receberá benefícios especiais.
              </Typography>
            </Paper>
          )}
        </Box>

        {/* Stepper */}
        <Paper elevation={0} sx={{ p: 3, mb: 3 }}>
          <Stepper activeStep={activeStep}>
            {steps.map((label) => (
              <Step key={label}>
                <StepLabel>{label}</StepLabel>
              </Step>
            ))}
          </Stepper>
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', textAlign: 'center', mt: 1.5 }}
          >
            ~2 minutos para completar
          </Typography>
        </Paper>

        {/* Step Content */}
        <Paper elevation={0} sx={{ p: 4, mb: 3 }}>
          {getStepContent(activeStep)}
        </Paper>

        {/* Navigation Buttons */}
        <Box display="flex" justifyContent="space-between">
          <Button
            onClick={activeStep === 0 ? () => navigate('/') : handleBack}
            disabled={isLoading}
            startIcon={<ArrowBackIcon />}
          >
            {activeStep === 0 ? 'Voltar ao Site' : 'Voltar'}
          </Button>

          <Box>
            {activeStep === steps.length - 1 ? (
              <Button
                variant="contained"
                color="primary"
                onClick={handleSignup}
                disabled={isLoading}
                size="large"
              >
                {isLoading ? 'Criando...' : 'Criar Organização'}
              </Button>
            ) : (
              <Button
                variant="contained"
                color="primary"
                onClick={handleNext}
                endIcon={<ArrowForwardIcon />}
                size="large"
              >
                Próximo
              </Button>
            )}
          </Box>
        </Box>

        {/* Login Link */}
        <Box textAlign="center" mt={4}>
          <Typography variant="body2" color="textSecondary">
            Já tem uma organização?{' '}
            <Button
              onClick={() => navigate('/login')}
              sx={{ textTransform: 'none' }}
              disabled={isLoading}
            >
              Fazer Login
            </Button>
          </Typography>
        </Box>
      </Box>
    </Box>
  );
};

export default OrganizationSignup;
